/**
 * Child Layout
 * Layout wrapper for child-facing pages with kid-friendly design
 */

import { ReactNode, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut } from 'lucide-react';
import { useAuthStore } from '../../hooks/useAuthStore';
import { useTimeOfDay } from '../../hooks/useTimeOfDay';
import { useMotionVariants } from '../../hooks/useReducedMotion';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { useAmbientSound } from '../../hooks/useAmbientSound';
import { useTouchFeedback } from '../../hooks/useTouchFeedback';
import { pageVariants, reducedPageVariants, springs } from '../../animation';
import MindoraCharacter from '../character/MindoraCharacter';
import { DecorativeCloud, WavyDivider } from '../decorations';

interface ChildLayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: '/learn', emoji: '🏠', label: 'Home' },
  { path: '/learn/chat', emoji: '💬', label: 'Chat' },
  { path: '/learn/progress', emoji: '⭐', label: 'Progress' },
  { path: '/learn/profile', emoji: '😊', label: 'Me' },
];

export default function ChildLayout({ children }: ChildLayoutProps) {
  const location = useLocation();
  const { logout } = useAuthStore();
  const variants = useMotionVariants(pageVariants, reducedPageVariants);

  // Activate time-of-day palette shifts on <html>
  useTimeOfDay();

  // Sound: navigation whoosh on route change (skip initial mount)
  const { play } = useSoundEffects();
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      play('navigate');
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, play]);

  // Ambient soundscapes
  useAmbientSound();

  // Touch feedback for links/non-buttons
  const touchFeedback = useTouchFeedback();

  const isActive = (path: string) => {
    if (path === '/learn') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-kid-bg texture-paper-grain">
      {/* Orientation advisory — landscape on small screens */}
      <div className="orientation-advisory fixed inset-0 z-50 bg-kid-bg/95 items-center justify-center flex-col gap-4 p-8 text-center">
        <MindoraCharacter mood="encouraging" size="xl" />
        <h2 className="text-kid-2xl font-display text-kid-text">Turn your device!</h2>
        <p className="text-kid-base text-kid-text-soft max-w-xs">
          Mindora works best when your screen is tall. Try holding it like a book!
        </p>
      </div>

      {/* Top Header — warm gradient with floating clouds */}
      <header className="relative bg-gradient-to-r from-kid-ocean/10 via-kid-violet/10 to-kid-berry/10 overflow-hidden">
        {/* Floating decorative clouds */}
        <DecorativeCloud size={90} variant="puffy" className="absolute -top-2 left-[10%] opacity-40" />
        <DecorativeCloud size={70} variant="wispy" className="absolute -top-1 right-[20%] opacity-30" />

        <div className="relative max-w-7xl mx-auto px-4 h-16 flex items-center justify-between z-10">
          <Link to="/learn" className="flex items-center gap-3 touch-kid touch-glow" onPointerDown={touchFeedback.onPointerDown}>
            <MindoraCharacter mood="idle" size="sm" />
            <span className="font-display text-kid-xl text-kid-text">
              Mindora
            </span>
          </Link>

          <button
            onClick={logout}
            onPointerDown={touchFeedback.onPointerDown}
            className="flex flex-col items-center gap-0.5 p-3 text-kid-text-soft hover:text-kid-text transition-colors rounded-xl hover:bg-kid-berry/10 touch-kid touch-glow touch-slop"
            title="Logout"
          >
            <LogOut className="w-6 h-6" strokeWidth={2.25} />
            <span className="text-xs font-bold leading-tight">Bye!</span>
          </button>
        </div>

        {/* Wavy divider replaces hard border */}
        <WavyDivider className="absolute bottom-0 left-0" amplitude={6} />
      </header>

      {/* Main Content — page transitions */}
      <main className="pb-24 kid-scrollbar overscroll-bouncy">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={springs.pageTurn}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Bottom Navigation — emoji icons */}
      <nav className="fixed bottom-0 left-0 right-0 bg-kid-surface/95 backdrop-blur-md border-t-2 border-kid-ocean/15 safe-area-pb z-40">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex gap-3 justify-center py-2">
            {navItems.map((item) => {
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onPointerDown={touchFeedback.onPointerDown}
                  className="flex flex-col items-center py-3 px-5 relative btn-squishy touch-kid touch-glow"
                >
                  {active && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full"
                      style={{
                        background: 'linear-gradient(90deg, var(--kid-ocean), var(--kid-violet))',
                      }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                  <span className={`text-3xl transition-transform ${active ? 'scale-110' : ''}`}>
                    {item.emoji}
                  </span>
                  <span
                    className={`text-xs mt-0.5 font-bold ${
                      active
                        ? 'text-kid-violet'
                        : 'text-kid-text-soft'
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
}
